import { api } from './api'

export interface PrintQueueStatus {
  organizationCode: string
  totalItems: number
  unprintedItems: number
  lists: number
  lastChecked: string
}

export interface QueueItem {
  id: number
  collector_list_id: number
  template_id: number | null
  articleNo: string
  ean: string
  name: string
  filename: string
  picture: string
  pdf: string
  html: string | null
  product: any | null
  is_printed: number
  created_at: string
  updated_at: string | null
}

export interface QueueList {
  id: number
  warehouse_id: number
  user_id: string
  name: string
  type: string
  create_status: number
  created_at: string
  updated_at: string
  items: QueueItem[]
}

export interface QueueDetails {
  result: boolean
  msg: string
  data: QueueList[]
}

export interface ClearQueueResult {
  organizationCode: string
  itemsProcessed: number
  itemsFailed: number
  errors: string[]
  dryRun: boolean
  completedAt: string
  message: string
}

export interface Organization {
  code: string
  name: string
}

export interface LegacySecrets {
  apiBaseUrl: string
  isConfigured: boolean
  availableCodes: Organization[]
}

export interface ClearingProgress {
  currentItem: number
  totalItems: number
  currentItemName?: string
  percentage: number
  isProcessing: boolean
}

// Print Server API service
export const printServerApi = {
  // Get runtime secrets and configuration
  getSecrets: async (): Promise<LegacySecrets> => {
    const response = await api.get('/admin/legacy-systems/secrets')
    return response.data.data
  },

  // Get available organizations
  getOrganizations: async (): Promise<Organization[]> => {
    const response = await api.get('/admin/legacy-systems/print-queue/organizations')
    return response.data.data
  },

  // Check print queue status for an organization
  checkQueueStatus: async (organizationCode: string): Promise<PrintQueueStatus> => {
    const response = await api.post('/admin/legacy-systems/print-queue/status', {
      organizationCode,
    })
    return response.data.data
  },

  // Get detailed queue information with all items
  getQueueDetails: async (organizationCode: string): Promise<QueueDetails> => {
    const response = await api.post('/admin/legacy-systems/print-queue/details', {
      organizationCode
    })
    // The backend returns { success: true, result: true, msg: 'OK', data: [...] }
    // We need to extract the queue details structure
    const { result, msg, data } = response.data
    return { result, msg, data }
  },

  // Clear print queue for an organization with progress tracking
  clearPrintQueue: async (
    organizationCode: string,
    dryRun: boolean = false,
    onProgress?: (progress: ClearingProgress) => void
  ): Promise<ClearQueueResult> => {
    // If progress callback is provided, use SSE endpoint for real-time updates
    if (onProgress && !dryRun) {
      // This would ideally use Server-Sent Events or WebSocket
      // For now, we'll use the standard endpoint
      const response = await api.post('/admin/legacy-systems/print-queue/clear', {
        organizationCode,
        dryRun,
        trackProgress: true
      })
      return response.data.data
    }
    
    const response = await api.post('/admin/legacy-systems/print-queue/clear', {
      organizationCode,
      dryRun,
    })
    return response.data.data
  },

  // Clear print queue using direct legacy API (if secrets are delivered)
  // This is an alternative approach if you decide to send temporary tokens
  clearPrintQueueDirect: async (
    apiUrl: string,
    apiKey: string,
    organizationCode: string
  ): Promise<any> => {
    // Implementation for direct API call
    // This would be similar to the original script
    // but running in the browser
    
    // For now, we'll use the backend proxy approach
    throw new Error('Direct API access not implemented. Use backend proxy.')
  },
}

export default printServerApi